import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { PostCard } from "@/components/PostCard";
import { PollCard } from "@/components/PollCard";
import { BoostedBanner } from "@/components/BoostedBanner";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, TrendingUp, ArrowLeft, MapPin, Music,
  ExternalLink, Coins, Send, Pencil, Check, X, Crown,
  Camera, ImagePlus,
  MessageCircle, Share2,
} from "lucide-react";
import { GenrePicker } from "@/components/GenrePicker";
import { ArtistPostsTab } from "@/components/profile/ArtistPostsTab";
import { ArtistPollsTab } from "@/components/profile/ArtistPollsTab";
import { ArtistInvestorsTab } from "@/components/profile/ArtistInvestorsTab";
import { toast } from "sonner";
import { SOCIAL_PLATFORMS } from "@/config/socialPlatforms";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.4, ease: "easeOut" } }),
};

export default function ArtistProfilePage() {
  const { id } = useParams();
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [posts, setPosts] = useState([]);
  const [polls, setPolls] = useState([]);
  const [topInvestors, setTopInvestors] = useState([]);
  const [totalInvested, setTotalInvested] = useState(0);
  const [loading, setLoading] = useState(true);
  const [betAmount, setBetAmount] = useState("");
  const [betLoading, setBetLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("posts");
  const [newPost, setNewPost] = useState({ content: "", embed_url: "" });
  const [postLoading, setPostLoading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [topFan, setTopFan] = useState(null);
  const coverInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await api.post("/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put(`/artists/${id}`, { cover_url: uploadRes.data.url });
      setArtist((prev) => ({ ...prev, cover_url: uploadRes.data.url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    }
    setCoverUploading(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const uploadRes = await api.post("/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put(`/artists/${id}`, { avatar_url: uploadRes.data.url });
      setArtist((prev) => ({ ...prev, avatar_url: uploadRes.data.url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    }
    setAvatarUploading(false);
  };

  const fetchArtist = useCallback(async () => {
    try {
      const [artistRes, pollsRes, investorsRes, topFanRes] = await Promise.all([
        api.get(`/artists/${id}`),
        api.get("/polls", { params: { artist_id: id, limit: 10 } }),
        api.get(`/investments/top-investors/${id}`).catch(() => ({ data: { top_investors: [], total_invested: 0 } })),
        api.get(`/topfan/${id}`).catch(() => ({ data: { top_fan: null } })),
      ]);
      setArtist(artistRes.data.artist);
      setPosts(artistRes.data.posts);
      setPolls(pollsRes.data.polls);
      setTopInvestors(investorsRes.data.top_investors);
      setTotalInvested(investorsRes.data.total_invested);
      setTopFan(topFanRes.data.top_fan);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, id]);

  useEffect(() => {
    fetchArtist();
  }, [fetchArtist]);

  const handleFollow = async () => {
    try {
      const res = await api.post(`/artists/${id}/follow`);
      setArtist((a) => ({
        ...a,
        is_following: res.data.following,
        fan_count: res.data.fan_count,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleLike = async (postId) => {
    const res = await api.post(`/posts/${postId}/like`);
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, is_liked: res.data.liked, likes_count: res.data.likes_count }
          : p
      )
    );
    refreshUser();
  };

  const handleReact = async (postId, reactionType) => {
    const res = await api.post(`/posts/${postId}/react`, { reaction_type: reactionType });
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, user_reaction: res.data.reaction_type, reactions: res.data.reactions }
          : p
      )
    );
    refreshUser();
    return res.data;
  };

  const handleVotePoll = async (pollId, optionId) => {
    const res = await api.post(`/polls/${pollId}/vote`, { option_id: optionId });
    setPolls((prev) => prev.map((p) => (p.id === pollId ? res.data.poll : p)));
    return res.data.poll;
  };

  const handleBet = async () => {
    const amount = parseInt(betAmount);
    if (!amount || amount <= 0) return;
    setBetLoading(true);
    try {
      await api.post("/investments", { artist_id: id, amount });
      setBetAmount("");
      refreshUser();
      fetchArtist();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBetLoading(false);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.content.trim() && !newPost.embed_url.trim()) return;
    setPostLoading(true);
    try {
      const type = newPost.embed_url ? "video" : "text";
      const res = await api.post("/posts", {
        content: newPost.content,
        embed_url: newPost.embed_url || null,
        type,
      });
      setPosts((prev) => [res.data.post, ...prev]);
      setNewPost({ content: "", embed_url: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setPostLoading(false);
    }
  };

  const startEditing = () => {
    setEditForm({
      name: artist.name || "",
      bio: artist.bio || "",
      city: artist.city || "",
      genres: artist.genres || [],
      social_links: artist.social_links || {},
    });
    setEditing(true);
  };

  const saveArtistProfile = async () => {
    setEditSaving(true);
    try {
      const res = await api.put(`/artists/${id}`, editForm);
      setArtist(res.data.artist);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setEditSaving(false);
    }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/artist/${id}`;
    const text = `Decouvre ${artist?.name} sur ReallyFan !`;
    if (navigator.share) {
      navigator.share({ title: "ReallyFan", text, url });
    } else {
      navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Lien copie !");
    }
  };

  if (loading) {
    return (
      <div className="py-6 space-y-4">
        <div className="bg-foreground/5 rounded-3xl h-56 animate-pulse" />
        <div className="bg-foreground/5 rounded-3xl h-32 animate-pulse" />
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="py-6 text-center">
        <p className="text-muted-foreground">Artiste non trouve</p>
      </div>
    );
  }

  const isOwner = user?.artist_id === artist.id;
  const scoreColor = artist.current_score >= 1.5
    ? "text-emerald-400"
    : artist.current_score >= 1.0
    ? "text-[#FF6B35]"
    : "text-red-400";

  const socialLinks = artist.social_links || {};
  const hasSocialLinks = Object.values(socialLinks).some((v) => v);

  const tabs = [
    { id: "posts", label: "Publications", count: posts.length },
    { id: "polls", label: "Sondages", count: polls.length },
    { id: "investors", label: "Miseurs", count: topInvestors.length },
  ];

  return (
    <div className="py-6 space-y-5" data-testid="artist-profile-page">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-[#AFA8BA] transition-colors"
        data-testid="artist-back-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour
      </button>

      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-card border border-border rounded-3xl overflow-hidden"
      >
        {/* Cover */}
        <div className="h-48 bg-gradient-to-br from-[#FF6B35]/30 via-[#7000FF]/20 to-[#0F0A1F] relative group">
          {artist.cover_url && (
            <img src={artist.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          {isOwner && (
            <>
              <button
                onClick={() => coverInputRef.current?.click()}
                className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 text-white text-[10px] font-bold opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
                data-testid="cover-upload-label"
              >
                {coverUploading ? "Upload..." : <><ImagePlus className="w-3.5 h-3.5" /> Banniere</>}
              </button>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleCoverUpload}
                disabled={coverUploading}
                data-testid="cover-upload-input"
              />
            </>
          )}

          {/* Score Badge */}
          <div className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm">
            <TrendingUp className={`w-3.5 h-3.5 ${scoreColor}`} />
            <span className={`font-mono-credits font-bold text-sm ${scoreColor}`}>
              {artist.current_score?.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="px-5 pb-5 -mt-12 relative">
          {/* Avatar */}
          <div className="relative group" data-testid="artist-avatar-container">
            <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center border-4 border-card shadow-xl overflow-hidden">
              {artist.avatar_url ? (
                <img src={artist.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-heading font-bold text-4xl">
                  {artist.name[0].toUpperCase()}
                </span>
              )}
            </div>
            {isOwner && (
              <>
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  className="absolute inset-0 flex items-center justify-center rounded-3xl bg-black/0 group-hover:bg-black/40 transition-colors"
                  data-testid="artist-avatar-upload-btn"
                >
                  <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  disabled={avatarUploading}
                  data-testid="artist-avatar-upload-input"
                />
              </>
            )}
            {avatarUploading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-3xl bg-black/50">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Artist Info */}
          <div className="mt-3">
            <AnimatePresence mode="wait">
              {editing ? (
                <motion.div
                  key="editing"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-3"
                  data-testid="edit-artist-form"
                >
                  <Input
                    placeholder="Nom d'artiste"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="bg-muted border-border rounded-xl text-card-foreground placeholder:text-muted-foreground h-10 font-heading font-bold"
                    data-testid="edit-artist-name"
                  />
                  <textarea
                    placeholder="Bio"
                    value={editForm.bio}
                    onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                    className="w-full bg-muted border border-border rounded-xl px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] outline-none resize-none h-20"
                    data-testid="edit-artist-bio"
                  />
                  <Input
                    placeholder="Ville"
                    value={editForm.city}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="bg-muted border-border rounded-xl text-card-foreground placeholder:text-muted-foreground h-10 text-sm"
                    data-testid="edit-artist-city"
                  />
                  {/* Social Links */}
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">Reseaux sociaux</p>
                    {SOCIAL_PLATFORMS.map(({ key, label, icon: Icon }) => (
                      <div key={key} className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                        <Input
                          placeholder={label}
                          value={editForm.social_links?.[key] || ""}
                          onChange={(e) => setEditForm({
                            ...editForm,
                            social_links: { ...editForm.social_links, [key]: e.target.value },
                          })}
                          className="bg-muted border-border rounded-xl text-card-foreground placeholder:text-muted-foreground h-9 text-xs"
                          data-testid={`edit-social-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Genres</p>
                    <GenrePicker
                      selected={editForm.genres || []}
                      onChange={(genres) => setEditForm({ ...editForm, genres })}
                      dark={true}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={saveArtistProfile}
                      disabled={editSaving}
                      className="flex items-center gap-1 bg-[#FF6B35] text-white font-bold px-5 py-2.5 rounded-full text-xs hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
                      data-testid="save-artist-btn"
                    >
                      <Check className="w-3 h-3" />
                      {editSaving ? "..." : "Enregistrer"}
                    </button>
                    <button
                      onClick={() => setEditing(false)}
                      className="flex items-center gap-1 bg-muted text-muted-foreground px-5 py-2.5 rounded-full text-xs border border-border"
                    >
                      <X className="w-3 h-3" /> Annuler
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="display"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h1 className="font-heading font-extrabold text-2xl text-card-foreground">{artist.name}</h1>
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/30" data-testid="artist-role-badge">Artiste</span>
                      {artist.verified && (
                        <svg className="w-5 h-5 text-[#7000FF] shrink-0" viewBox="0 0 24 24" fill="currentColor" data-testid="verified-badge"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isOwner && (
                        <button
                          onClick={startEditing}
                          className="p-2 rounded-xl bg-muted text-muted-foreground hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all"
                          data-testid="edit-artist-btn"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={handleShare}
                        className="p-2 rounded-xl bg-muted text-muted-foreground hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all"
                        data-testid="share-artist-btn"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Meta info */}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    {artist.city && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3" /> {artist.city}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="w-3 h-3" /> {artist.fan_count} fans
                    </span>
                  </div>

                  {artist.bio && (
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{artist.bio}</p>
                  )}

                  {/* Genres */}
                  {artist.genres?.length > 0 && (
                    <div className="flex gap-1.5 mt-3 flex-wrap">
                      {artist.genres.map((g) => (
                        <span key={g} className="text-[10px] px-2.5 py-1 rounded-full bg-muted text-card-foreground/70 border border-border">
                          {g}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Social Links */}
                  {hasSocialLinks && (
                    <div className="flex gap-2 mt-3">
                      {SOCIAL_PLATFORMS.map(({ key, icon: Icon, url: makeUrl }) => {
                        const val = socialLinks[key];
                        if (!val) return null;
                        return (
                          <a
                            key={key}
                            href={makeUrl(val)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center text-muted-foreground hover:text-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all border border-border hover:border-[#FF6B35]/20"
                            data-testid={`artist-social-${key}`}
                          >
                            <Icon className="w-4 h-4" />
                          </a>
                        );
                      })}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-5">
                    {!isOwner && (
                      <>
                        <button
                          onClick={handleFollow}
                          className={`flex-1 py-3 rounded-full text-sm font-bold transition-all duration-300 ${
                            artist.is_following
                              ? "bg-muted text-card-foreground/70 border border-border hover:bg-border"
                              : "bg-gradient-to-r from-[#FF6B35] to-[#FF8FA3] text-white shadow-[0_0_20px_rgba(255,107,53,0.3)] hover:shadow-[0_0_30px_rgba(255,107,53,0.5)] hover:scale-[1.02] active:scale-95"
                          }`}
                          data-testid="artist-follow-btn"
                        >
                          {artist.is_following ? "Suivi" : "Suivre"}
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const res = await api.post("/messages/conversations", { recipient_id: artist.user_id });
                              navigate(`/messages/${res.data.conversation.id}`);
                            } catch (err) { console.error(err); }
                          }}
                          className="px-5 py-3 rounded-full text-sm font-bold bg-[#7000FF]/80 text-white border border-[#7000FF]/30 hover:bg-[#7000FF] transition-all"
                          data-testid="artist-message-btn"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Stats Bar */}
      <motion.div
        custom={1}
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-3 gap-3"
      >
        <div className="bg-card border border-border rounded-2xl p-3 text-center shadow-sm">
          <Users className="w-4 h-4 text-[#7000FF] mx-auto mb-1" />
          <p className="font-mono-credits font-bold text-lg text-card-foreground">{artist.fan_count}</p>
          <p className="text-[9px] text-muted-foreground">Fans</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-3 text-center shadow-sm">
          <TrendingUp className={`w-4 h-4 mx-auto mb-1 ${scoreColor}`} />
          <p className={`font-mono-credits font-bold text-lg ${scoreColor}`}>{artist.current_score?.toFixed(2)}</p>
          <p className="text-[9px] text-muted-foreground">Score</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-3 text-center shadow-sm">
          <Coins className="w-4 h-4 text-[#FF6B35] mx-auto mb-1" />
          <p className="font-mono-credits font-bold text-lg text-card-foreground">{totalInvested.toLocaleString()}</p>
          <p className="text-[9px] text-muted-foreground">Investis</p>
        </div>
      </motion.div>

      {/* Top Fan of the Week */}
      {topFan && (
        <motion.div
          custom={1.5}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className={`border rounded-2xl p-4 shadow-sm flex items-center gap-3 ${
            topFan.is_me
              ? "bg-gradient-to-r from-amber-50 to-yellow-50 border-yellow-300"
              : "bg-card border-border"
          }`}
          data-testid="top-fan-section"
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            topFan.is_me ? "bg-yellow-100" : "bg-[#FF6B35]/10"
          }`}>
            <Crown className={`w-5 h-5 ${topFan.is_me ? "text-yellow-500" : "text-[#FF6B35]"}`} />
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#7000FF] flex items-center justify-center overflow-hidden shrink-0">
              {topFan.avatar_url ? (
                <img src={topFan.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-xs font-bold">{(topFan.pseudo || "?")[0].toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-bold">Top Fan de la semaine</p>
              <p className={`font-heading font-bold text-sm truncate ${topFan.is_me ? "text-yellow-700" : "text-card-foreground"}`}>
                {topFan.is_me ? "C'est toi !" : topFan.pseudo}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="font-mono-credits font-bold text-sm text-[#FF6B35]">{topFan.score}</p>
            <p className="text-[9px] text-muted-foreground">points</p>
          </div>
        </motion.div>
      )}

      {/* Ad Placement */}
      <BoostedBanner placement="sidebar" />

      {/* Bet Widget */}
      {!isOwner && user && (
        <motion.div
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="bg-card border border-border rounded-3xl p-5 shadow-sm"
          data-testid="bet-widget"
        >
          <h3 className="font-heading font-bold text-sm text-card-foreground mb-3 flex items-center gap-2">
            <Coins className="w-4 h-4 text-[#FF6B35]" />
            Miser sur {artist.name}
          </h3>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-muted-foreground">
              Cote : <span className={`font-mono-credits font-bold ${scoreColor}`}>{artist.current_score?.toFixed(2)}</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Solde : <span className="font-mono-credits font-bold text-card-foreground">{user.credits_balance}</span>
            </p>
          </div>
          <div className="flex gap-1.5 mb-3">
            {[10, 50, 100, 500].map((amt) => (
              <button
                key={amt}
                onClick={() => setBetAmount(String(amt))}
                disabled={user.credits_balance < amt}
                className={`flex-1 text-xs py-2 rounded-full border transition-all font-mono-credits ${
                  betAmount === String(amt)
                    ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35] font-bold"
                    : "bg-white border-border text-muted-foreground hover:border-[#FF6B35]/20"
                } disabled:opacity-30`}
              >
                {amt}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder="Montant"
              min="1"
              max={user.credits_balance}
              value={betAmount}
              onChange={(e) => setBetAmount(e.target.value)}
              className="bg-white border-border rounded-xl text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-10 font-mono-credits"
              data-testid="bet-amount-input"
            />
            <button
              onClick={handleBet}
              disabled={betLoading || !betAmount || parseInt(betAmount) > user.credits_balance}
              className="bg-[#FF6B35] text-white font-bold px-6 rounded-full hover:scale-105 active:scale-95 transition-transform duration-200 disabled:opacity-50 text-sm shrink-0"
              data-testid="bet-submit-btn"
            >
              {betLoading ? "..." : "Miser"}
            </button>
          </div>
        </motion.div>
      )}

      {/* Tabs */}
      <motion.div
        custom={3}
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        className="flex gap-1 bg-foreground/5 rounded-2xl p-1 border border-foreground/5"
        data-testid="artist-tabs"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 ${
              activeTab === tab.id
                ? "bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/20"
                : "text-muted-foreground hover:text-[#AFA8BA]"
            }`}
            data-testid={`artist-tab-${tab.id}`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`text-[9px] font-mono-credits ${activeTab === tab.id ? "text-[#FF6B35]" : "text-muted-foreground"}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </motion.div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === "posts" && (
          <ArtistPostsTab
            posts={posts}
            setPosts={setPosts}
            isOwner={isOwner}
            newPost={newPost}
            setNewPost={setNewPost}
            postLoading={postLoading}
            handleCreatePost={handleCreatePost}
            handleLike={handleLike}
            handleReact={handleReact}
          />
        )}

        {activeTab === "polls" && (
          <ArtistPollsTab polls={polls} handleVotePoll={handleVotePoll} />
        )}

        {activeTab === "investors" && (
          <ArtistInvestorsTab topInvestors={topInvestors} totalInvested={totalInvested} />
        )}
      </AnimatePresence>
    </div>
  );
}
